<template>
  <div class="p-6 max-w-4xl mx-auto flex flex-col h-screen">
    
    <div class="flex items-center gap-4 mb-6 flex-shrink-0">
      <button @click="$router.push('/')" class="p-2 hover:bg-gray-100 rounded-full transition-colors dark:hover:bg-gray-700">
        <ArrowLeftIcon :size="24" class="text-gray-600 dark:text-gray-300"/>
      </button>
      <h1 class="text-3xl font-bold text-gray-800 dark:text-white">Configuración</h1>
    </div>

    <div class="flex flex-col md:flex-row gap-6 flex-1 min-h-0">
      
      <div class="w-full md:w-1/4 bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex flex-col min-h-0">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white flex-shrink-0">Categorías</h2>
        
        <ul class="space-y-1 flex-1 overflow-y-auto pr-1 custom-scrollbar min-h-0">
          <li 
            v-for="category in categories" 
            :key="category" 
            @click="selectCategory(category)"
            :class=" [
              'px-3 py-2 rounded-md cursor-pointer transition-colors text-sm font-medium',
              selectedCategory === category 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:bg-opacity-40 dark:text-blue-300' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
          >
            {{ category }}
          </li>
        </ul>
      </div>

      <div class="flex-1 w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col min-h-0">
        
        <div class="flex justify-between items-center mb-6 flex-shrink-0">
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <DatabaseIcon :size="20"/>
              Campos: <span class="text-blue-600 dark:text-blue-400">{{ selectedCategory }}</span>
            </h2>
        </div>
        
        <div class="flex flex-col md:flex-row gap-4 mb-6 items-end bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border dark:border-gray-600 flex-shrink-0">
          
          <div class="flex-1 w-full">
            <label class="block text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">Nombre del Campo</label>
            <input 
              v-model="newField.label"
              @input="generateKey"
              type="text"
              placeholder="Ej: Precio Venta"
              class="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
            >
            <p v-if="newField.key" class="text-xs text-gray-400 mt-1 font-mono flex items-center gap-1">
               Key: <span class="text-gray-600 dark:text-gray-300 font-bold">{{ newField.key }}</span>
            </p>
          </div>

          <div class="w-full md:w-48 flex-shrink-0">
             <label class="block text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">Tipo de Dato</label>
             <select 
                v-model="newField.tipo"
                class="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-colors"
             >
                <option value="text">Texto</option>
                <option value="number">Número</option>
                <option value="date">Fecha</option>
             </select>
          </div>

          <button 
            @click="handleAddField"
            :disabled="!newField.label || !newField.key || isProcessing"
            class="bg-blue-600 text-white px-4 h-10 rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center flex-shrink-0"
            style="min-width: 100px;"
          >
            <span v-if="isProcessing" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
            {{ isProcessing ? '...' : 'Añadir' }}
          </button>
        </div>

        <div v-if="loading" class="text-center py-10 text-gray-500 dark:text-gray-400 flex-shrink-0">
            <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-current mb-2"></div>
            <p>Cargando campos...</p>
        </div>
        
        <div v-else class="space-y-2 flex-1 overflow-y-auto pr-2 custom-scrollbar min-h-0">
          <div v-if="filteredFields.length === 0" class="text-center py-10 text-gray-400 dark:text-gray-500 italic border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            No hay campos configurados para la categoría "{{ selectedCategory }}".
          </div>
          
          <div 
            v-for="field in filteredFields" 
            :key="field.id"
            class="flex items-center justify-between p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-all group"
          >
            <div class="flex items-center gap-3">
                <div class="p-2 rounded-lg flex-shrink-0 flex items-center justify-center h-10 w-10" 
                    :class="{
                        'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:bg-opacity-50 dark:text-purple-200': field.tipo === 'date',
                        'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:bg-opacity-50 dark:text-yellow-200': field.tipo === 'number',
                        'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:bg-opacity-50 dark:text-blue-200': field.tipo === 'text' || !field.tipo
                    }">
                  <CalendarIcon v-if="field.tipo === 'date'" :size="20"/>
                  <HashIcon v-else-if="field.tipo === 'number'" :size="20"/>
                  <TypeIcon v-else :size="20"/>
                </div>

                <div>
                  <div class="flex items-center gap-2 flex-wrap">
                      <p class="font-bold text-gray-800 dark:text-gray-100 text-sm">{{ field.label }}</p>
                      
                      <span class="text-xs px-2 py-0.5 rounded-full border font-semibold tracking-wide"
                        :class="{
                            'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-200 dark:border-purple-800': field.tipo === 'date',
                            'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-200 dark:border-yellow-800': field.tipo === 'number',
                            'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-200 dark:border-blue-800': field.tipo === 'text' || !field.tipo
                        }">
                        {{ formatTipo(field.tipo) }}
                      </span>

                      <span v-if="field.categoria" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded ml-2 border border-gray-200 dark:border-gray-600">
                          {{ field.categoria }}
                      </span>
                  </div>
                  <p class="text-xs text-gray-400 dark:text-gray-500 font-mono mt-1">Key: {{ field.key }}</p>
                </div>
            </div>
            
            <button 
              @click="handleDeleteField(field)"
              :disabled="isProcessing"
              title="Eliminar campo"
              class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-2 rounded hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20"
            >
              <Trash2Icon :size="18"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useFields } from '../composables/useFields';
import { FIELD_CATEGORIES, FIELD_TYPE_LABELS } from '../constants/documentTypes';
// IMPORTANTE: Importar los iconos nuevos
import { 
    ArrowLeft as ArrowLeftIcon, Database as DatabaseIcon, Trash2 as Trash2Icon,
    Type as TypeIcon, Calendar as CalendarIcon, Hash as HashIcon 
} from 'lucide-vue-next';

// Extraemos 'error' para mostrar mensajes detallados
const { fields, loading, error, fetchFields, createField, deleteField, formatTipo: formatTipoGlobal } = useFields();

const formatTipo = (t) => {
    if (formatTipoGlobal) return formatTipoGlobal(t);
    return FIELD_TYPE_LABELS[t] || 'Texto';
};

const newField = ref({ label: '', key: '', tipo: 'text' });
const categories = ref([...FIELD_CATEGORIES]);
const selectedCategory = ref('Todos');
const isProcessing = ref(false);

const generateKey = () => {
    if (!newField.value.label) {
        newField.value.key = '';
        return;
    }
    newField.value.key = newField.value.label
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
        .replace(/[^a-z0-9\s-_]/g, '') 
        .trim()
        .replace(/\s+/g, '_');
};

const handleAddField = async () => {
  if (!newField.value.label || !newField.value.key) return;
  
  isProcessing.value = true;
  try {
      const success = await createField({ 
        label: newField.value.label, 
        key: newField.value.key, 
        tipo: newField.value.tipo, 
        // Asignación de categoría
        categoria: selectedCategory.value === 'Todos' ? null : selectedCategory.value,
        orden: fields.value.length 
      });
      
      if (success) {
          newField.value = { label: '', key: '', tipo: 'text' };
          await fetchFields();
      } else {
          alert(`No se pudo crear el campo: ${error.value || 'Error desconocido'}`);
      }
  } finally {
      isProcessing.value = false;
  }
};

const handleDeleteField = async (field) => {
  if (!field || !field.id) return;

  if (!confirm(`¿Estás seguro de eliminar el campo "${field.label}"?`)) return;

  isProcessing.value = true;
  try {
    const success = await deleteField(field.id);
    if (!success) {
      alert(`Error al eliminar: ${error.value || 'Error desconocido'}`);
    }
  } catch (err) {
    console.error(err);
    alert("Error de conexión al eliminar.");
  } finally {
    isProcessing.value = false;
  }
};

const filteredFields = computed(() => {
  if (selectedCategory.value === 'Todos') return fields.value;
  return fields.value.filter(field => field.categoria === selectedCategory.value);
});

const selectCategory = (category) => {
  selectedCategory.value = category;
};

onMounted(() => {
    fetchFields();
    
    // --- Persistencia de Modo Oscuro al recargar ---
    if (localStorage.getItem('theme') === 'dark' || 
       (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
});
</script>

<style scoped>
/* Estilos personalizados para el scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
